import { Column, Entity, Index, OneToMany } from 'typeorm';
import { AutomationBatchTargetListEntity } from './automation-batch-target-list.entity';
import { AutomationTargetListItemEntity } from './automation-target-list-item.entity';
import { BaseEntity } from './base.entity';

/**
 * Static target list for automation batches.
 * Contains manually curated items with custom columns defined in metadata.
 *
 * Note: Dynamic lists have been replaced by direct references to Groups and Saved Lists
 * via AutomationBatchTargetListEntity. Target lists are now always static.
 */
@Entity('_automation_target_list')
export class AutomationTargetListEntity extends BaseEntity {
	@Column({
		name: '_name',
		type: 'varchar',
		length: 255,
	})
	public name!: string;

	@Column({
		name: '_description',
		type: 'text',
		nullable: true,
	})
	public description?: string;

	/**
	 * Column definitions for the static list items.
	 * Defines what fields can be entered for each item.
	 * Structure: { columns: Array<{ id: string, name: string, type: string }> }
	 */
	@Column({
		name: '_metadata',
		type: 'json',
		nullable: true,
		comment: 'Column definitions for static list items',
	})
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public metadata?: Record<string, any>;

	@Column({
		name: '_is_active',
		type: 'boolean',
		default: true,
	})
	public isActive!: boolean;

	// === LEGACY FIELDS (kept for backwards compatibility during migration) ===
	// These fields are no longer used but kept to avoid breaking existing data.
	// They can be removed in a future migration after all dynamic lists are deleted.

	@Column({
		name: '_list_type',
		type: 'enum',
		enum: ['static', 'dynamic'],
		default: "'static'",
	})
	public listType!: 'static' | 'dynamic';

	@Column({
		name: '_entity_type',
		type: 'varchar',
		length: 50,
		nullable: true,
	})
	public entityType?: string | null;

	@Column({
		name: '_query',
		type: 'json',
		nullable: true,
	})
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	public query?: Record<string, any>;

	// === Relations ===

	@OneToMany(() => AutomationTargetListItemEntity, (item) => item.targetList)
	public items!: Array<AutomationTargetListItemEntity>;

	@OneToMany(
		() => AutomationBatchTargetListEntity,
		(batchList) => batchList.targetList,
	)
	public batchLists!: Array<AutomationBatchTargetListEntity>;

	// === Indexes ===

	@Index('idx_automation_target_list_active')
	public static isActiveIndex: [string] = ['isActive'];

	// Legacy indexes - kept for existing data
	@Index('idx_automation_target_list_type')
	public static listTypeIndex: [string] = ['listType'];

	@Index('idx_automation_target_list_entity_type')
	public static entityTypeIndex: [string] = ['entityType'];
}
